import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { ArrowLeft, Clock, AlertCircle } from 'lucide-react';

const EVENT_LABEL = {
  logout: 'Signed out', login: 'Signed in',
  search_performed: 'Searched', conversation_started: 'Started chat',
  profile_viewed: 'Viewed profile', profile_viewed_by: 'Profile was viewed',
  availability_changed: 'Availability change', phone_revealed: 'Phone revealed',
  phone_reveal_denied: 'Phone reveal denied', contact_requested: 'Contact requested',
  contact_request_decided: 'Contact decided', account_created: 'Account created',
  email_verified: 'Email verified', profile_completed: 'Profile completed',
  worker_became_searchable: 'Became searchable',
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
    });
  } catch { return iso; }
}

function fmtDay(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/London',
    });
  } catch { return iso; }
}

function groupByDay(rows) {
  const groups = {};
  rows.forEach((r) => {
    const key = (r.created_at || '').slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  return Object.entries(groups).sort(([a], [b]) => (a < b ? 1 : -1));
}

function RowLine({ row }) {
  const search = row.event_type === 'search_performed' ? row.meta || {} : null;
  return (
    <div className="flex gap-3 items-start py-2 border-l-2 border-slate-200 pl-3" data-testid="user-activity-row">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 font-medium">{EVENT_LABEL[row.event_type] || row.event_type}</div>
        {search && (
          <div className="text-xs text-slate-600">
            {search.trade || '—'}
            {search.outward_postcode ? ` · ${search.outward_postcode}` : ''}
            {' · '}
            <span className={search.zero_results ? 'text-red-600' : ''}>{search.results_count} result{search.results_count === 1 ? '' : 's'}</span>
          </div>
        )}
        {row.event_type === 'conversation_started' && (
          <div className="text-xs text-slate-600">→ {row.target_user_name || row.meta?.target_user_id}</div>
        )}
        {row.event_type === 'profile_viewed' && (
          <div className="text-xs text-slate-600">→ {row.target_user_name || row.meta?.target_user_id}</div>
        )}
        {row.event_type === 'profile_viewed_by' && (
          <div className="text-xs text-slate-600">by {row.user_name || row.user_id}</div>
        )}
        {row.event_type === 'phone_revealed' && (
          <div className="text-xs text-slate-600">→ {row.target_user_name || row.meta?.target_user_id} ({row.meta?.mode})</div>
        )}
        {row.event_type === 'phone_reveal_denied' && (
          <div className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />Rate limit — retry {row.meta?.retry_after_seconds}s</div>
        )}
        {row.event_type === 'availability_changed' && (
          <div className="text-xs text-slate-600">{row.meta?.action} · {row.meta?.date_blocked}</div>
        )}
      </div>
      <div className="text-xs text-slate-400 whitespace-nowrap" data-testid="user-activity-time">{fmtTime(row.created_at).split(', ')[1] || ''}</div>
    </div>
  );
}

export default function AdminUserActivity({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const from = new Date(Date.now() - days * 86400000).toISOString();
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/activity/users/${userId}?from=${encodeURIComponent(from)}&limit=500`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="text-sm text-slate-500">Loading timeline…</div>;
  if (error) return <div className="text-sm text-red-600" data-testid="user-activity-error">{error}</div>;
  if (!data) return null;

  const groups = groupByDay(data.rows || []);
  return (
    <div className="space-y-4" data-testid="admin-user-activity">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-sm text-orange-600 flex items-center gap-1 hover:underline" data-testid="user-activity-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white font-bold text-lg flex items-center justify-center">
            {(data.user.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-slate-900" data-testid="user-activity-name">{data.user.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
              <span className="uppercase tracking-wide">{data.user.user_type}</span>
              {data.user.business_name && <span>· {data.user.business_name}</span>}
              <span>· {data.user.user_id}</span>
              {data.user.approval_status && <span>· {data.user.approval_status}</span>}
            </div>
            {data.last_activity_at && (
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Last activity {fmtTime(data.last_activity_at)}
              </div>
            )}
          </div>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                  className="border border-slate-300 rounded-md px-2 py-1 text-sm"
                  data-testid="user-activity-days">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="user-activity-empty">
          No recorded activity in this window.
        </div>
      )}

      {groups.map(([day, rows]) => (
        <div key={day} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">{fmtDay(day + 'T00:00:00Z')}</div>
          {rows.map((r, i) => <RowLine key={`${r.source}-${r.created_at}-${i}`} row={r} />)}
        </div>
      ))}
    </div>
  );
}
